// Shared ClipCard — the exact icon strip used on the Library, reusable
// on any page that renders a clip (galleries, performance detail, rider/
// horse/owner profiles, etc.).
//
// Two flavours exported:
//   • <ClipCard clip={..} on..={..} />           — presentational; caller wires handlers
//   • <ClipCardWithActions clip={..} onChanged={fn} /> — self-contained; provides its
//     own delete / feedback / edit / preview + rename / save-to-disk / duplicate.
//
// Extracted verbatim from Library.js so behaviour is identical.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Calendar, Check, CheckSquare, Copy, Download, ExternalLink,
  FolderHeart, HardDrive, Link2, Loader2, Pencil, Play, Scissors, Share2,
  Square, ThumbsDown, ThumbsUp, Trash2, Video, X,
} from "lucide-react";

import { api, BACKEND_URL, CATEGORIES, CATEGORY_NAME, fmtDuration } from "@/lib/api";
import { saveClipToDisk } from "@/lib/saveToDisk";
import AutocompleteInput from "@/components/AutocompleteInput";
import RenameModal from "@/components/RenameModal";


export async function copyClipDirectUrl(clipId) {
  try {
    const r = await api.get(`/clips/${clipId}/presigned-url`);
    const { url, thumbnail_url, filename } = r.data;
    const clipboardText = thumbnail_url
      ? `Video: ${url}\nThumbnail: ${thumbnail_url}`
      : url;
    try {
      await navigator.clipboard.writeText(clipboardText);
      toast.success(
        thumbnail_url ? "Video + thumbnail URLs copied · 7 days" : "Direct URL copied · 7 days",
        { description: filename },
      );
    } catch {
      window.prompt("Copy this URL (Ctrl/Cmd+C):", clipboardText);
    }
  } catch (err) {
    toast.error("Failed to generate URL: " + (err?.response?.data?.detail || err.message));
  }
}


export function LazyClipPreview({ src, alt, clipId }) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  useEffect(() => {
    if (shouldLoad || !ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShouldLoad(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: "300px" }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [shouldLoad]);
  return (
    <div ref={ref} className="absolute inset-0">
      {shouldLoad ? (
        <img
          src={`${BACKEND_URL}/api/clips/${clipId}/thumbnail`}
          className="w-full h-full object-cover"
          loading="lazy"
          alt={alt}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="w-full h-full bg-stone-900 flex items-center justify-center">
          <Play size={36} className="text-stone-700" fill="currentColor" />
        </div>
      )}
    </div>
  );
}


export function VimeoButton({ clip }) {
  const status = clip.vimeo_status;
  const url = clip.vimeo_url;
  const busy = status && ["queued", "downloading", "creating", "uploading"].includes(status);
  const done = status === "done" && url;
  const failed = status === "failed";

  const upload = async (e) => {
    e.stopPropagation();
    try {
      await api.post("/clips/vimeo-upload", { clip_ids: [clip.id] });
      toast.success("Vimeo upload queued — track progress on Jobs page");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to queue");
    }
  };

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this clip from Vimeo? The original stays in your Library.")) return;
    try {
      await api.delete(`/clips/${clip.id}/vimeo`);
      toast.success("Removed from Vimeo");
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  if (done) {
    return (
      <div className="inline-flex border border-sky-700 rounded-sm overflow-hidden" data-testid={`vimeo-link-${clip.id}`}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="px-2 py-1.5 text-sky-400 hover:bg-sky-600/10 inline-flex items-center gap-1 text-[11px]"
          title={`Open on Vimeo: ${url}`}
        >
          <Video size={11} /> Vimeo <ExternalLink size={9} />
        </a>
        <button
          onClick={remove}
          className="px-1.5 py-1.5 text-stone-500 hover:text-rose-400 border-l border-sky-700"
          title="Remove from Vimeo"
          data-testid={`vimeo-remove-${clip.id}`}
        >
          <X size={11} />
        </button>
      </div>
    );
  }

  if (busy) {
    const pct = clip.vimeo_progress || 0;
    return (
      <button
        disabled
        className="btn-secondary !py-1.5 !px-2 inline-flex items-center gap-1 text-[11px] !text-sky-400 !border-sky-700"
        title={`${status} · ${pct}%`}
        data-testid={`vimeo-progress-${clip.id}`}
      >
        <Loader2 size={11} className="animate-spin" /> {pct}%
      </button>
    );
  }

  return (
    <button
      onClick={upload}
      className={`btn-secondary !py-1.5 !px-2 hover:!text-sky-400 ${failed ? "!text-rose-400" : ""}`}
      title={failed ? `Vimeo upload failed: ${clip.vimeo_error || "unknown"} — click to retry` : "Send to Vimeo"}
      data-testid={`vimeo-btn-${clip.id}`}
    >
      <Video size={12} />
    </button>
  );
}


export function AssignGalleryButton({ clipId, clip }) {
  // Universal "Assign to…" popover. Mirrors Library exactly.
  const TABS = [
    { key: "galleries",     label: "Galleries",     multi: true,  patchField: null },
    { key: "associations",  label: "Associations",  multi: false, patchField: "association" },
    { key: "events",        label: "Performance",   multi: false, patchField: "event_name" },
    { key: "riders",        label: "Riders",        multi: false, patchField: "rider_horse_name" },
    { key: "horses",        label: "Horses",        multi: false, patchField: "horse_name" },
    { key: "owners",        label: "Owners",        multi: false, patchField: "owner_name" },
    { key: "photographers", label: "Photographers", multi: false, patchField: "photographer_name" },
    { key: "videographers", label: "Videographers", multi: false, patchField: "videographer_name" },
    { key: "announcers",    label: "Announcers",    multi: false, patchField: "announcer_name" },
  ];

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("galleries");
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [galleriesOn, setGalleriesOn] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [rowSearch, setRowSearch] = useState("");
  const ref = useRef(null);
  // Render as a fixed centered dialog when the viewport is narrower than
  // 1100px (iPads, split-screen laptops, phones). At that width the 520px
  // popover anchored to the far-left column of a card grid overflows off
  // the viewport's left edge and clips the row list.
  const [isCompact, setIsCompact] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1100 : false,
  );
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < 1100);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadTab = async (key) => {
    if (tabData[key]) return;
    setTabLoading((m) => ({ ...m, [key]: true }));
    try {
      let rows = [];
      if (key === "galleries") {
        const [g, m] = await Promise.all([
          api.get("/galleries"),
          api.get(`/clips/${clipId}/galleries`),
        ]);
        rows = (g.data.galleries || []).map((x) => ({ id: x.id, name: x.name }));
        setGalleriesOn(new Set((m.data.galleries || []).map((x) => x.id)));
      } else if (key === "events") {
        const r = await api.get("/events");
        rows = (r.data.events || []).map((e) => ({ name: e.name, count: e.clip_count }));
      } else if (key === "associations") {
        const r = await api.get("/associations");
        rows = (r.data.associations || []).map((a) => ({ name: a.name, count: a.clip_count }));
      } else if (key === "riders") {
        const r = await api.get("/riders");
        rows = (r.data.riders || []).map((x) => ({ name: x.name, count: x.clip_count }));
      } else if (key === "horses") {
        const r = await api.get("/horses");
        rows = (r.data.horses || []).map((x) => ({ name: x.name, count: x.clip_count }));
      } else if (key === "owners") {
        const r = await api.get("/owners");
        rows = (r.data.owners || []).map((x) => ({ name: x.name, count: x.clip_count }));
      } else if (key === "photographers") {
        const r = await api.get("/directory/photographer");
        rows = (Array.isArray(r.data) ? r.data : []).map((x) => ({ id: x.id, name: x.name }));
      } else if (key === "videographers") {
        const r = await api.get("/directory/videographer");
        rows = (Array.isArray(r.data) ? r.data : []).map((x) => ({ id: x.id, name: x.name }));
      } else if (key === "announcers") {
        const r = await api.get("/directory/announcer");
        rows = (Array.isArray(r.data) ? r.data : []).map((x) => ({ id: x.id, name: x.name }));
      }
      setTabData((m) => ({ ...m, [key]: rows }));
    } catch (err) {
      toast.error(`Failed to load ${key}`);
    } finally {
      setTabLoading((m) => ({ ...m, [key]: false }));
    }
  };

  const toggle = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setOpen(true);
    setActiveTab("galleries");
    await loadTab("galleries");
  };

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const flipGallery = (id) => {
    setGalleriesOn((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const saveGalleries = async () => {
    setSaving(true);
    try {
      await api.put(`/clips/${clipId}/galleries`, { gallery_ids: Array.from(galleriesOn) });
      toast.success(`Assigned to ${galleriesOn.size} galler${galleriesOn.size === 1 ? "y" : "ies"}`);
      setOpen(false);
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const assignSingle = async (tab, name) => {
    if (!tab.patchField) return;
    setBusyRow(name);
    try {
      await api.patch(`/clips/${clipId}`, { [tab.patchField]: name });
      toast.success(`Assigned ${tab.label.slice(0, -1) || tab.label} · ${name}`);
      setOpen(false);
    } catch (err) {
      toast.error("Assign failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusyRow(null);
    }
  };

  const currentTab = TABS.find((t) => t.key === activeTab) || TABS[0];
  const rows = tabData[activeTab] || [];
  const loadingTab = !!tabLoading[activeTab];
  const currentValue = currentTab.patchField ? (clip?.[currentTab.patchField] || "") : "";
  const filteredRows = useMemo(() => {
    const s = rowSearch.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.name || "").toLowerCase().includes(s));
  }, [rows, rowSearch]);
  // Reset the row-search when switching tabs so each tab starts fresh.
  useEffect(() => { setRowSearch(""); }, [activeTab]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="btn-secondary !py-1.5 !px-2 hover:!text-orange-400"
        title="Assign to gallery, event, rider, horse, owner, photographer, videographer or announcer"
        data-testid={`clip-galleries-${clipId}`}
      >
        <FolderHeart size={12} />
      </button>
      {open && (isCompact ? (
        // iPad / phone: full-screen centered dialog with tap-outside backdrop
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-3"
          onClick={() => setOpen(false)}
          data-testid={`assign-popover-${clipId}`}
        >
          <div
            className="bg-stone-950 border border-orange-700 w-full max-w-[540px] max-h-[85vh] flex flex-col shadow-xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="absolute top-1.5 right-1.5 z-10 bg-black/80 hover:bg-black text-white rounded-full p-1.5 border border-stone-700"
              aria-label="Close"
              data-testid={`assign-popover-close-${clipId}`}
            >
              <X size={13} />
            </button>
            <div className="flex flex-1 min-h-0">
              <AssignBody
                TABS={TABS} activeTab={activeTab} setActiveTab={setActiveTab}
                loadTab={loadTab} currentTab={currentTab} currentValue={currentValue}
                rowSearch={rowSearch} setRowSearch={setRowSearch}
                filteredRows={filteredRows} loadingTab={loadingTab} rows={rows}
                galleriesOn={galleriesOn} flipGallery={flipGallery}
                assignSingle={assignSingle} busyRow={busyRow} saveGalleries={saveGalleries}
                saving={saving} setOpen={setOpen} clipId={clipId}
                compact
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className="absolute right-0 top-full mt-1 w-[520px] bg-stone-950 border border-stone-700 shadow-xl z-30 flex"
          onClick={(e) => e.stopPropagation()}
          data-testid={`assign-popover-${clipId}`}
        >
          <AssignBody
            TABS={TABS} activeTab={activeTab} setActiveTab={setActiveTab}
            loadTab={loadTab} currentTab={currentTab} currentValue={currentValue}
            rowSearch={rowSearch} setRowSearch={setRowSearch}
            filteredRows={filteredRows} loadingTab={loadingTab} rows={rows}
            galleriesOn={galleriesOn} flipGallery={flipGallery}
            assignSingle={assignSingle} busyRow={busyRow} saveGalleries={saveGalleries}
            saving={saving} setOpen={setOpen} clipId={clipId}
          />
        </div>
      ))}
    </div>
  );
}


// Extracted so the popover (desktop, absolute) and the fullscreen dialog
// (iPad / phone) both render identical content.
function AssignBody({
  TABS, activeTab, setActiveTab, loadTab, currentTab, currentValue,
  rowSearch, setRowSearch, filteredRows, loadingTab, rows,
  galleriesOn, flipGallery, assignSingle, busyRow, saveGalleries, saving,
  setOpen, clipId, compact = false,
}) {
  return (
    <>
      <div className={`shrink-0 border-r border-stone-800 bg-stone-950 py-1 ${compact ? "w-28" : "w-32"}`}>
        <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-stone-500">Assign to</div>
        {TABS.map((t) => {
          const active = t.key === activeTab;
          return (
            <button
              key={t.key}
              onClick={() => { setActiveTab(t.key); loadTab(t.key); }}
              className={`w-full text-left px-2 py-1.5 text-[11px] transition-colors ${
                active
                  ? "bg-orange-950/40 text-orange-300 border-l-2 border-orange-500"
                  : "text-stone-400 hover:bg-stone-900 hover:text-stone-200 border-l-2 border-transparent"
              }`}
              data-testid={`assign-tab-${t.key}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-800 flex items-center justify-between">
          <span>{currentTab.label}</span>
          {currentValue && (
            <span className="text-orange-400 truncate max-w-[180px]" title={`Current: ${currentValue}`}>
              · {currentValue}
            </span>
          )}
        </div>
        {rows.length > 5 && (
          <div className="px-2 py-1.5 border-b border-stone-800">
            <input
              type="text"
              value={rowSearch}
              onChange={(e) => setRowSearch(e.target.value)}
              placeholder={`Search ${currentTab.label.toLowerCase()}…`}
              className="field-input w-full !py-1 !text-[11px]"
              data-testid={`assign-search-${currentTab.key}`}
              autoFocus
            />
          </div>
        )}
        <div className={`flex-1 overflow-y-auto py-1 ${compact ? "min-h-[240px]" : "min-h-[220px] max-h-[300px]"}`}>
          {loadingTab ? (
            <div className="text-stone-500 text-xs px-3 py-4">Loading…</div>
          ) : filteredRows.length === 0 ? (
            <div className="text-stone-500 text-xs px-3 py-4">
              {rowSearch
                ? `No ${currentTab.label.toLowerCase()} match "${rowSearch}"`
                : `No ${currentTab.label.toLowerCase()} yet.`}
            </div>
          ) : currentTab.multi ? (
            filteredRows.map((r) => {
              const on = galleriesOn.has(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => flipGallery(r.id)}
                  title={r.name}
                  className={`w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-900 ${on ? "text-orange-400" : "text-stone-300"}`}
                  data-testid={`gallery-toggle-${r.id}`}
                >
                  <span className={`w-4 h-4 border ${on ? "border-orange-500 bg-orange-600/30" : "border-stone-600"} inline-flex items-center justify-center shrink-0`}>
                    {on && <Check size={10} className="text-orange-400" />}
                  </span>
                  <span className="truncate">{r.name}</span>
                </button>
              );
            })
          ) : (
            filteredRows.map((r, i) => {
              const isCurrent = r.name && r.name === currentValue;
              const busy = busyRow === r.name;
              return (
                <button
                  key={r.id || r.name || i}
                  onClick={() => assignSingle(currentTab, r.name)}
                  disabled={busy}
                  title={r.name}
                  className={`w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-900 disabled:opacity-50 ${isCurrent ? "text-orange-400 bg-orange-950/20" : "text-stone-300"}`}
                  data-testid={`assign-row-${currentTab.key}-${(r.id || r.name || i).toString().replace(/\s+/g, "_")}`}
                >
                  <span className={`w-3 h-3 rounded-full border ${isCurrent ? "border-orange-500 bg-orange-600" : "border-stone-600"} shrink-0`} />
                  <span className="truncate flex-1">{r.name}</span>
                  {r.count != null && <span className="text-[10px] font-mono-tc text-stone-500 shrink-0">{r.count}</span>}
                  {busy && <Loader2 size={11} className="animate-spin text-orange-400 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
        {currentTab.multi && rows.length > 0 && (
          <div className="flex gap-2 border-t border-stone-800 p-2">
            <button onClick={() => setOpen(false)} className="btn-secondary !py-1 !px-2 text-[10px] flex-1">Cancel</button>
            <button onClick={saveGalleries} disabled={saving} className="btn-primary !py-1 !px-2 text-[10px] flex-1" data-testid={`gallery-save-${clipId}`}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
        {!currentTab.multi && currentValue && (
          <div className="border-t border-stone-800 p-2">
            <button
              onClick={() => assignSingle(currentTab, "")}
              className="btn-secondary !py-1 !px-2 text-[10px] w-full hover:!text-rose-400"
              data-testid={`assign-clear-${currentTab.key}`}
            >
              Clear {currentTab.label.slice(0, -1) || currentTab.label}
            </button>
          </div>
        )}
      </div>
    </>
  );
}


export function EditClipModal({ clip, onClose, onSaved }) {
  const [subLabel, setSubLabel] = useState(clip.sub_label || "");
  const [rider, setRider] = useState(clip.rider_horse_name || "");
  const [heeler, setHeeler] = useState(clip.heeler_name || "");
  const [horse, setHorse] = useState(clip.horse_name || "");
  const [notes, setNotes] = useState(clip.notes || "");
  const [category, setCategory] = useState(clip.category || "");
  const [association, setAssociation] = useState(clip.association || "");
  const [performanceName, setPerformanceName] = useState(clip.performance_name || clip.event_name || "");
  const [eventName, setEventName] = useState(clip.event_name || "");
  const [ownerName, setOwnerName] = useState(clip.owner_name || "");
  const [photographer, setPhotographer] = useState(clip.photographer_name || "");
  const [videographer, setVideographer] = useState(clip.videographer_name || "");
  const [announcer, setAnnouncer] = useState(clip.announcer_name || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sub_label: subLabel,
        rider_horse_name: rider,
        heeler_name: heeler,
        horse_name: horse,
        notes,
        category,
        association,
        performance_name: performanceName,
        event_name: eventName,
        owner_name: ownerName,
        photographer_name: photographer,
        videographer_name: videographer,
        announcer_name: announcer,
      };
      const res = await api.patch(`/clips/${clip.id}`, payload);
      toast.success("Clip updated");
      onSaved({ id: clip.id, ...payload, ...res.data });
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-modal">
      <div className="bg-stone-950 border border-stone-700 max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800 shrink-0">
          <div className="font-display uppercase text-lg">Edit Clip</div>
          <button onClick={onClose} className="btn-secondary" data-testid="edit-close"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-5 overflow-y-auto">
          <div>
            <div className="label-tiny mb-2 text-orange-500">Hierarchy</div>
            <div className="space-y-3 border border-stone-800 p-3">
              <div>
                <label className="label-tiny block mb-1">Association <span className="text-stone-500 normal-case">(umbrella org)</span></label>
                <AutocompleteInput field="association" value={association} onChange={setAssociation} className="field-input" placeholder="e.g. CRA Chinook Rodeo Association" data-testid="edit-association" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Performance <span className="text-stone-500 normal-case">(specific rodeo / show)</span></label>
                <AutocompleteInput field="performance_name" value={performanceName} onChange={setPerformanceName} className="field-input" placeholder="e.g. 2026 Chinook Rodeo Finals - Perf #3" data-testid="edit-performance" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Event discipline</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="field-input" data-testid="edit-category">
                  {CATEGORIES.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div>
                <label className="label-tiny block mb-1">Event name <span className="text-stone-500 normal-case">(legacy — falls back to performance)</span></label>
                <AutocompleteInput field="event_name" value={eventName} onChange={setEventName} className="field-input" placeholder="Leave blank to inherit from performance" data-testid="edit-event-name" />
              </div>
            </div>
          </div>

          <div>
            <div className="label-tiny mb-2 text-orange-500">Riders / Horses</div>
            <div className="space-y-3 border border-stone-800 p-3">
              <div>
                <label className="label-tiny block mb-1">Rider / Header</label>
                <AutocompleteInput field="rider" value={rider} onChange={setRider} className="field-input" placeholder="Sarah Johnson" data-testid="edit-rider" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Heeler <span className="text-stone-500 normal-case">(team roping)</span></label>
                <AutocompleteInput field="heeler" value={heeler} onChange={setHeeler} className="field-input" placeholder="Jake Miller" data-testid="edit-heeler" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Horse</label>
                <AutocompleteInput field="horse" value={horse} onChange={setHorse} className="field-input" placeholder="Dusty" data-testid="edit-horse" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Horse owner</label>
                <AutocompleteInput field="owner" value={ownerName} onChange={setOwnerName} className="field-input" placeholder="Bar J Ranch" data-testid="edit-owner" />
              </div>
            </div>
          </div>

          <div>
            <div className="label-tiny mb-2 text-orange-500">Crew</div>
            <div className="space-y-3 border border-stone-800 p-3">
              <div>
                <label className="label-tiny block mb-1">Photographer</label>
                <AutocompleteInput field="photographer" value={photographer} onChange={setPhotographer} className="field-input" placeholder="Jamie Hughes Photography" data-testid="edit-photographer" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Videographer</label>
                <AutocompleteInput field="videographer" value={videographer} onChange={setVideographer} className="field-input" placeholder="Rodeo Cut Studio" data-testid="edit-videographer" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Announcer</label>
                <AutocompleteInput field="announcer" value={announcer} onChange={setAnnouncer} className="field-input" placeholder="Announcer name" data-testid="edit-announcer" />
              </div>
            </div>
          </div>

          <div>
            <div className="label-tiny mb-2 text-orange-500">Sub-label & Notes</div>
            <div className="space-y-3 border border-stone-800 p-3">
              <div>
                <label className="label-tiny block mb-1">Sub-label</label>
                <AutocompleteInput field="sub_label" value={subLabel} onChange={setSubLabel} className="field-input" placeholder="clean run, no-time, broke barrier..." data-testid="edit-sublabel" />
              </div>
              <div>
                <label className="label-tiny block mb-1">Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="field-input" placeholder="Run details, what made it good/bad, training cues..." data-testid="edit-notes" />
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-stone-800 flex gap-2 shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="edit-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="edit-save">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


// Lightweight video-play modal used by ClipCardWithActions. Not the full Studio
// PreviewModal (which has trim controls) — this one just plays the clip.
function ClipPlayModal({ clip, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="clip-play-modal"
    >
      <div
        className="bg-stone-950 border border-stone-700 w-full max-w-3xl max-h-[92vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 bg-black/80 hover:bg-black text-white rounded-full p-2 border border-stone-700"
          title="Close (Esc)"
          data-testid="clip-play-close"
          aria-label="Close preview"
        >
          <X size={16} />
        </button>
        <div className="px-4 py-3 border-b border-stone-800 pr-14 shrink-0">
          <div className="label-tiny">{CATEGORY_NAME[clip.category] || clip.category}</div>
          <div className="font-display uppercase text-lg truncate">
            {clip.rider_horse_name || clip.sub_label || clip.source_video_name || "Clip"}
            {clip.heeler_name ? ` & ${clip.heeler_name}` : ""}
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-black flex items-center justify-center overflow-hidden">
          <video
            src={clip.stream_url || `${BACKEND_URL}/api/clips/${clip.id}/stream`}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-full object-contain"
            data-testid="clip-play-video"
          />
        </div>
      </div>
    </div>
  );
}


export function ClipCard({ clip, onPreview, onDelete, onFeedback, onEdit, onSaveToDisk, onEditInStudio, onDuplicateAsSource, onRename, selectMode, selected, onToggleSelect, onFilterAssociation, onFilterEvent }) {
  return (
    <div className="border border-stone-800 hover:border-orange-700 transition-colors group" data-testid={`clip-card-${clip.id}`}>
      <div className="block w-full aspect-video bg-black relative overflow-hidden">
        <button
          onClick={selectMode ? onToggleSelect : onPreview}
          className="absolute inset-0 z-0"
          data-testid={`clip-preview-${clip.id}`}
          aria-label="Preview clip"
        >
          <LazyClipPreview
            src={clip.stream_url || `${BACKEND_URL}/api/clips/${clip.id}/stream`}
            alt={clip.sub_label || "clip"}
            clipId={clip.id}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Play size={32} className="text-white" fill="white" />
          </div>
        </button>
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start max-w-[calc(100%-1rem)] z-10 pointer-events-none">
          {clip.is_master ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-amber-950/85 border border-amber-500 text-amber-300 backdrop-blur-sm font-semibold"
              data-testid={`clip-master-badge-${clip.id}`}
              title="Master recording — full uncut source video"
            >
              <Video size={10} /> MASTER
            </span>
          ) : (
            <span className="tag-cat backdrop-blur-sm bg-stone-900/80">{CATEGORY_NAME[clip.category]}</span>
          )}
          {(clip.association || clip.event_name) && (
            <div className="flex flex-wrap items-center gap-1 pointer-events-auto" data-testid={`clip-tagged-badge-${clip.id}`}>
              {clip.association && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onFilterAssociation?.(clip.association); }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-orange-950/85 border border-orange-600 text-orange-300 hover:bg-orange-900 hover:border-orange-400 hover:text-white backdrop-blur-sm max-w-[180px] cursor-pointer transition-colors"
                  title={`Filter by association: ${clip.association}`}
                  data-testid={`clip-association-pill-${clip.id}`}
                >
                  <Building2 size={9} className="shrink-0" />
                  <span className="truncate">{clip.association}</span>
                </button>
              )}
              {clip.event_name && clip.event_name.toLowerCase() !== (clip.association || "").toLowerCase() && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onFilterEvent?.(clip.event_name); }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-stone-900/85 border border-stone-600 text-stone-300 hover:bg-stone-800 hover:border-orange-400 hover:text-white backdrop-blur-sm max-w-[180px] cursor-pointer transition-colors"
                  title={`Filter by event: ${clip.event_name}`}
                  data-testid={`clip-event-pill-${clip.id}`}
                >
                  <Calendar size={9} className="shrink-0" />
                  <span className="truncate">{clip.event_name}</span>
                </button>
              )}
            </div>
          )}
        </div>
        {selectMode && (
          <div className="absolute top-2 right-2 z-10 pointer-events-none">
            {selected ? <CheckSquare size={20} className="text-orange-500 bg-stone-900/80 rounded" /> : <Square size={20} className="text-stone-300 bg-stone-900/60" />}
          </div>
        )}
        <div className="absolute bottom-2 right-2 font-mono-tc text-xs text-orange-400 bg-black/70 px-2 py-0.5 z-10 pointer-events-none">
          {fmtDuration(clip.duration)}
        </div>
      </div>
      <div className="p-3">
        {clip.rider_horse_name || clip.heeler_name ? (
          <>
            <div className="flex items-center gap-1.5 group/name">
              <div className="font-display uppercase font-semibold text-base leading-tight truncate flex-1" title={[clip.rider_horse_name, clip.heeler_name].filter(Boolean).join(" & ")}>
                {clip.rider_horse_name}{clip.rider_horse_name && clip.heeler_name ? " & " : ""}{clip.heeler_name}
              </div>
              {onRename && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRename(); }}
                  className="text-stone-500 hover:text-orange-400 p-0.5 opacity-0 group-hover/name:opacity-100 focus:opacity-100 transition-opacity"
                  title="Rename sub-label"
                  data-testid={`clip-rename-btn-${clip.id}`}
                >
                  <Pencil size={11} />
                </button>
              )}
            </div>
            {clip.horse_name && (
              <div className="text-xs text-stone-400 mt-0.5 truncate" title={`Horse: ${clip.horse_name}`}>🐎 {clip.horse_name}</div>
            )}
            {clip.sub_label && (
              <div className="text-xs uppercase tracking-wider text-orange-400/90 mt-1 truncate">{clip.sub_label}</div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5 group/name">
              <div className="font-display uppercase font-semibold text-base leading-tight truncate flex-1" title={clip.sub_label || clip.source_video_name}>
                {clip.sub_label || clip.source_video_name || "Untitled"}
              </div>
              {onRename && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRename(); }}
                  className="text-stone-500 hover:text-orange-400 p-0.5 opacity-0 group-hover/name:opacity-100 focus:opacity-100 transition-opacity"
                  title="Rename this clip"
                  data-testid={`clip-rename-btn-${clip.id}`}
                >
                  <Pencil size={11} />
                </button>
              )}
            </div>
            {clip.horse_name && (
              <div className="text-xs text-stone-400 mt-0.5 truncate" title={`Horse: ${clip.horse_name}`}>🐎 {clip.horse_name}</div>
            )}
          </>
        )}
        {clip.notes && (
          <div className="text-xs text-stone-500 mt-1 italic line-clamp-2">{clip.notes}</div>
        )}
        <div className="mt-3 pt-3 border-t border-stone-800 space-y-2">
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={(e) => { e.stopPropagation(); onFeedback("good"); }}
              className={`p-1.5 transition-colors ${clip.feedback === "good" ? "text-emerald-400" : "text-stone-500 hover:text-emerald-400"}`}
              title="Good cut — train AI to make more like this"
              data-testid={`clip-thumbs-up-${clip.id}`}
            >
              <ThumbsUp size={14} fill={clip.feedback === "good" ? "currentColor" : "none"} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onFeedback("bad"); }}
              className={`p-1.5 transition-colors ${clip.feedback === "bad" ? "text-rose-400" : "text-stone-500 hover:text-rose-400"}`}
              title="Bad cut — train AI to avoid this"
              data-testid={`clip-thumbs-down-${clip.id}`}
            >
              <ThumbsDown size={14} fill={clip.feedback === "bad" ? "currentColor" : "none"} />
            </button>
            <a
              href={`${BACKEND_URL}/api/clips/${clip.id}/download`}
              className="btn-secondary !py-1.5 !px-2 ml-auto inline-flex items-center justify-center"
              data-testid={`clip-download-${clip.id}`}
              title="Download to browser downloads folder"
            >
              <Download size={12} />
            </a>
            <button
              onClick={(e) => { e.stopPropagation(); copyClipDirectUrl(clip.id); }}
              className="btn-secondary !py-1.5 !px-2 hover:!text-sky-400"
              title="Copy direct S3 URL — paste into TikTok / Instagram / YouTube import"
              data-testid={`clip-copy-url-${clip.id}`}
            >
              <Link2 size={12} />
            </button>
            {onSaveToDisk && (
              <button
                onClick={(e) => { e.stopPropagation(); onSaveToDisk(); }}
                className="btn-secondary !py-1.5 !px-2 hover:!text-emerald-400"
                title="Save to disk with full metadata filename"
                data-testid={`clip-save-to-disk-${clip.id}`}
              >
                <HardDrive size={12} />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="btn-secondary !py-1.5 !px-2 hover:!text-orange-400"
              title="Edit rider, label, notes"
              data-testid={`clip-edit-${clip.id}`}
            >
              <Pencil size={12} />
            </button>
            {onEditInStudio && (
              <button
                onClick={(e) => { e.stopPropagation(); onEditInStudio(); }}
                className="btn-secondary !py-1.5 !px-2 hover:!text-orange-400"
                title="Open the source video in Studio scrolled to this clip's range"
                data-testid={`clip-edit-in-studio-${clip.id}`}
              >
                <Scissors size={12} />
              </button>
            )}
            {onDuplicateAsSource && (
              <button
                onClick={(e) => { e.stopPropagation(); onDuplicateAsSource(); }}
                className="btn-secondary !py-1.5 !px-2 hover:!text-orange-400"
                title="Duplicate this clip as a new Studio source video — cut sub-clips from it"
                data-testid={`clip-duplicate-as-source-${clip.id}`}
              >
                <Copy size={12} />
              </button>
            )}
            <AssignGalleryButton clipId={clip.id} clip={clip} />
            <VimeoButton clip={clip} />
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const r = await api.post(`/clips/${clip.id}/share`);
                  const url = `${window.location.origin}/share/${r.data.token}`;
                  await navigator.clipboard.writeText(url);
                  toast.success("Share link copied to clipboard");
                } catch (err) {
                  toast.error("Failed to create share link");
                }
              }}
              className="btn-secondary !py-1.5 !px-2 hover:!text-orange-400"
              title="Copy public share link"
              data-testid={`clip-share-${clip.id}`}
            >
              <Share2 size={12} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="btn-secondary !py-1.5 !px-2 hover:!text-rose-500"
              title="Delete"
              data-testid={`clip-delete-${clip.id}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Self-contained wrapper — drop this on any page rendering a clip and get
 * the full Library icon strip + working preview / edit / delete out of the
 * box. Consumer only needs to pass `clip` and an `onChanged` reload callback.
 *
 * Optional overrides let callers customise behaviour (e.g. pass onDelete to
 * remove-from-gallery instead of delete-from-library).
 */
export function ClipCardWithActions({
  clip,
  onChanged,
  onDelete: onDeleteOverride,
  onFilterAssociation,
  onFilterEvent,
}) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(false);
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const handleDelete = async () => {
    if (onDeleteOverride) return onDeleteOverride();
    if (!window.confirm("Delete this clip?")) return;
    try {
      await api.delete(`/clips/${clip.id}`);
      toast.success("Clip deleted");
      onChanged?.();
    } catch (err) {
      toast.error("Delete failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleFeedback = async (fb) => {
    const newFb = clip.feedback === fb ? null : fb;
    try {
      await api.put(`/clips/${clip.id}/feedback`, { feedback: newFb });
      onChanged?.();
    } catch (err) {
      toast.error("Feedback failed");
    }
  };

  const handleEditInStudio = () => {
    if (!clip.video_id) {
      toast.error("This clip has no source video");
      return;
    }
    const qs = new URLSearchParams({
      seek: String(clip.start_time ?? 0),
      in: String(clip.start_time ?? 0),
      out: String(clip.end_time ?? 0),
    }).toString();
    navigate(`/studio/${clip.video_id}?${qs}`);
  };

  const handleDuplicateAsSource = async () => {
    try {
      const r = await api.post(`/clips/${clip.id}/duplicate-as-source`);
      const newVid = r.data;
      if (!newVid?.id) throw new Error("no video returned");
      toast.success("Clip duplicated as a new Studio source");
      navigate(`/studio/${newVid.id}`);
    } catch (e) {
      toast.error("Duplicate failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSaveToDisk = async () => {
    const ev = clip.event_name || clip.source_video_name || "";
    try {
      const r = await saveClipToDisk(clip, ev);
      toast.success(`Saved ${r.filename}`);
    } catch (e) {
      toast.error("Save failed: " + (e?.message || e));
    }
  };

  return (
    <>
      <ClipCard
        clip={clip}
        onPreview={() => setPreview(true)}
        onDelete={handleDelete}
        onFeedback={handleFeedback}
        onEdit={() => setEditing(true)}
        onSaveToDisk={handleSaveToDisk}
        onEditInStudio={handleEditInStudio}
        onDuplicateAsSource={handleDuplicateAsSource}
        onRename={() => setRenaming(true)}
        onFilterAssociation={onFilterAssociation || ((v) => navigate(`/library?association=${encodeURIComponent(v)}`))}
        onFilterEvent={onFilterEvent || ((v) => navigate(`/library?event=${encodeURIComponent(v)}`))}
      />
      {preview && <ClipPlayModal clip={clip} onClose={() => setPreview(false)} />}
      {editing && (
        <EditClipModal
          clip={clip}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged?.(); }}
        />
      )}
      {renaming && (
        <RenameModal
          label="Rename Clip"
          currentName={clip.sub_label || ""}
          subtitle="Updates the clip's sub-label."
          testIdPrefix="clip-rename"
          maxLen={120}
          onSave={async (newName) => {
            await api.patch(`/clips/${clip.id}`, { sub_label: newName });
            toast.success("Clip renamed");
            onChanged?.();
          }}
          onClose={() => setRenaming(false)}
        />
      )}
    </>
  );
}
